import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';

import Country from '../../../models/Country';
import Currency from '../../../models/Currency';
import Language from '../../../models/Language';
import { modelOf } from '../../../prop-types';
import ConfigStore from '../../../store/ConfigStore';
import CountryStore from '../../../store/CountryStore';
import CurrencyStore from '../../../store/CurrencyStore';
import LanguageStore from '../../../store/LanguageStore';
import RegionalSettingsCurrencyDropdown from '../RegionalSettingsCurrencyDropdown';
import RegionalSettingsDeliveryCountryDropdown from '../RegionalSettingsDeliveryCountryDropdown';
import RegionalSettingsLanguageDropdown from '../RegionalSettingsLanguageDropdown';
import { getRegionalSettingsDropdownMenuClassName } from './menuPresentation';

const RegionalSettingsDropdowns = ({
  configStore,
  languageStore,
  currencyStore,
  countryStore,
  country,
  language,
  currency,
  onChange,
  disabled,
  menuContainer,
}) => {
  const showCountries = countryStore.countries.length > 1;
  const showLanguages = languageStore.languages.length > 1;
  const showCurrencies = currencyStore.currencies.length > 1;
  const selectorCount = [showCountries, showLanguages, showCurrencies].filter(
    Boolean
  ).length;
  const menuClassName = menuContainer
    ? getRegionalSettingsDropdownMenuClassName(configStore.layout.corners)
    : undefined;

  const countryDropdown = showCountries ? (
    <RegionalSettingsDeliveryCountryDropdown
      key="country"
      country={country}
      countries={countryStore.countries}
      onSelect={(newCountry) => onChange('country', newCountry)}
      disabled={disabled}
      positionFixed
      menuContainer={menuContainer}
      menuClassName={menuClassName}
    />
  ) : null;
  const languageDropdown = showLanguages ? (
    <RegionalSettingsLanguageDropdown
      key="language"
      language={language}
      languages={languageStore.languages}
      onSelect={(newLanguage) => onChange('language', newLanguage)}
      disabled={disabled}
      positionFixed
      menuContainer={menuContainer}
      menuClassName={menuClassName}
    />
  ) : null;
  const currencyDropdown = showCurrencies ? (
    <RegionalSettingsCurrencyDropdown
      key="currency"
      currency={currency}
      currencies={currencyStore.currencies}
      onSelect={(newCurrency) => onChange('currency', newCurrency)}
      disabled={disabled}
      positionFixed
      menuContainer={menuContainer}
      menuClassName={menuClassName}
    />
  ) : null;

  const topRowDropdowns = [languageDropdown, currencyDropdown].filter(Boolean);
  const topRowHasSingleDropdown = topRowDropdowns.length === 1;

  if (topRowDropdowns.length === 0 && !countryDropdown) {
    return null;
  }

  return (
    <div
      className={classNames('RegionalSettingsDropdowns', {
        'RegionalSettingsDropdowns--two-selectors': selectorCount === 2,
      })}
    >
      {topRowDropdowns.length > 0 && (
        <div className="RegionalSettingsDropdowns__selector-row">
          {topRowDropdowns.map((dropdown) => (
            <div
              key={dropdown.key}
              className={`RegionalSettingsDropdowns__selector-cell${
                topRowHasSingleDropdown
                  ? ' RegionalSettingsDropdowns__selector-cell--full'
                  : ''
              }`}
            >
              {dropdown}
            </div>
          ))}
        </div>
      )}
      {countryDropdown && (
        <div className="RegionalSettingsDropdowns__selector-row RegionalSettingsDropdowns__selector-row--full">
          {countryDropdown}
        </div>
      )}
    </div>
  );
};

RegionalSettingsDropdowns.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  languageStore: modelOf(LanguageStore).isRequired,
  currencyStore: modelOf(CurrencyStore).isRequired,
  countryStore: modelOf(CountryStore).isRequired,
  country: modelOf(Country).isRequired,
  currency: modelOf(Currency).isRequired,
  language: modelOf(Language).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  menuContainer: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.func,
  ]),
};

RegionalSettingsDropdowns.defaultProps = {
  disabled: false,
  menuContainer: undefined,
};

export default inject(
  'configStore',
  'countryStore',
  'currencyStore',
  'languageStore'
)(observer(RegionalSettingsDropdowns));
